import type {
  SelectChangeEvent} from '@mui/material';

import React, { useState, useEffect, useCallback } from 'react';
import { format, isValid, subDays, endOfDay, startOfDay } from 'date-fns';

import {
  Today,
  Search,
  Cancel,
  Refresh,
  Download,
  Schedule,
  ExitToApp,
  AccessTime,
  CheckCircle
} from '@mui/icons-material';
import {
  Box,
  Card,
  Grid,
  Stack,
  Table,
  Alert,
  Button,
  Select,
  Avatar,
  Tooltip,
  MenuItem,
  TableRow,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  CardHeader,
  Typography,
  InputLabel,
  IconButton,
  CardContent,
  FormControl,
  TableContainer,
  LinearProgress,
  InputAdornment,
  TablePagination
} from '@mui/material';

import { attendanceApi, type Attendance, ATTENDANCE_REASON_LABELS_TR } from 'src/api/attendance.api';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import {
  formatDurationTr,
  attendanceStatusColor,
  attendanceStatusLabel,
  AUTO_CHECKOUT_LABEL_TR,
  AUTO_CHECKOUT_TOOLTIP_TR,
  durationMinutesFromTimestamps,
} from 'src/sections/attendance/utils/attendance-status';

// Extended interface for display purposes
interface AttendanceRecord extends Omit<Attendance, 'duration'> {
  employeeName?: string;
  employeeEmail?: string;
  duration?: number; // in minutes (override from ISO string)
  workHours?: number; // in hours
}

interface BranchAttendanceTabProps {
  branchId: string;
  branchName: string;
}

// Attendance status options
const ATTENDANCE_STATUS = [
  { value: '', label: 'Tüm Durumlar' },
  { value: 'PRESENT', label: 'Mevcut' },
  { value: 'ABSENT', label: 'Yok' },
  { value: 'LATE', label: 'Geç Geldi' },
  { value: 'EARLY_LEAVE', label: 'Erken Ayrıldı' },
  { value: 'HALF_DAY', label: 'Yarım Gün' },
  { value: 'OVERTIME', label: 'Mesai' },
  { value: 'COMPLETED', label: 'Tamamlandı' },
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'PENDING', label: 'Beklemede' },
];

const BranchAttendanceTab: React.FC<BranchAttendanceTabProps> = ({ branchId, branchName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter states - Start with last 7 days by default
  const [startDate, setStartDate] = useState<string>(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [searchEmployee, setSearchEmployee] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch attendance records
  const fetchAttendanceRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const startDateTime = startDate ? startOfDay(new Date(startDate)) : startOfDay(subDays(new Date(), 30));
      const endDateTime = endDate ? endOfDay(new Date(endDate)) : endOfDay(new Date());

      // Call the fixed backend API
      const records = await attendanceApi.getBranchAttendance(branchId, startDateTime, endDateTime);

      // Enrich records with employee information from the response
      const enrichedRecords: AttendanceRecord[] = records.map(record => {
        const employeeName = record.employee
          ? `${record.employee.firstName} ${record.employee.lastName}`
          : 'Bilinmeyen Çalışan';
        const employeeEmail = record.employee?.email || '';

        // Calculate duration ONLY from timestamps (backend duration field has bugs)
        let durationMinutes = 0;
        let workHours = 0;

        // Always calculate from checkInTime and checkOutTime
        if (record.checkInTime && record.checkOutTime) {
          const checkInTime = new Date(record.checkInTime);
          const checkOutTime = new Date(record.checkOutTime);

          if (!Number.isNaN(checkInTime.getTime()) && !Number.isNaN(checkOutTime.getTime())) {
            // Calculate difference in milliseconds, then convert to minutes
            const diffMs = checkOutTime.getTime() - checkInTime.getTime();
            durationMinutes = Math.floor(diffMs / (1000 * 60));
            // Convert to hours with 2 decimal places
            workHours = Math.round((durationMinutes / 60) * 100) / 100;
          }
        }

        return {
          ...record,
          employeeName,
          employeeEmail,
          duration: durationMinutes,
          workHours
        };
      });

      // Filter by employee search (client-side after API call)
      let filteredRecords = enrichedRecords;
      if (searchEmployee) {
        filteredRecords = enrichedRecords.filter(record =>
          record.employeeName?.toLowerCase().includes(searchEmployee.toLowerCase()) ||
          record.employeeEmail?.toLowerCase().includes(searchEmployee.toLowerCase())
        );
      }

      // Filter by status (client-side)
      if (statusFilter) {
        filteredRecords = filteredRecords.filter(record => record.status === statusFilter);
      }

      // Aktif (devam eden) kayıtlar EN ÜSTTE — yönetici "şu an kim içeride" sorusunu tıklar tıklamaz
      // görsün. Aktif = status ACTIVE ya da giriş var / çıkış yok. Sonra en yeni giriş üstte olacak
      // şekilde tarihe göre azalan.
      const isOngoing = (r: AttendanceRecord) =>
        r.status === 'ACTIVE' || (!!r.checkInTime && !r.checkOutTime);
      filteredRecords = [...filteredRecords].sort((a, b) => {
        const ao = isOngoing(a) ? 1 : 0;
        const bo = isOngoing(b) ? 1 : 0;
        if (ao !== bo) return bo - ao;
        const at = a.checkInTime ? new Date(a.checkInTime).getTime() : 0;
        const bt = b.checkInTime ? new Date(b.checkInTime).getTime() : 0;
        return bt - at;
      });

      setAttendanceRecords(filteredRecords);
    } catch (err: any) {
      console.error('Error fetching attendance records:', err);
      setError(err.message || 'Devam kayıtları yüklenirken bir hata oluştu');
      setAttendanceRecords([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, startDate, endDate, searchEmployee, statusFilter]);

  // Initial data load and reload when date filters change
  useEffect(() => {
    fetchAttendanceRecords();
  }, [fetchAttendanceRecords]);

  // Event handlers
  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchEmployee(event.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchAttendanceRecords();
  };

  // FE-3: date-fns v4 geçersiz/null tarihte RangeError atıp tabloyu beyaz ekran yapıyordu.
  // Guard'lı formatter: geçersiz/eksik değerde '—' döner, asla throw etmez.
  const safeFmt = (value: string | null | undefined, fmt: string): string => {
    if (!value) return '—';
    const d = new Date(value);
    return isValid(d) ? format(d, fmt) : '—';
  };

  const handleExport = () => {
    // FE-11: RFC 4180 kaçışı + formül-koruması (CSV injection) + UTF-8 BOM.
    // Her alan çift tırnakla sarılır, içteki " ikilenir; =,+,-,@ ile başlayan
    // değerler ' ile prefikslenir ki Excel/Sheets formül olarak çalıştırmasın.
    const esc = (value: unknown): string => {
      let s = String(value ?? '');
      if (/^[=+\-@]/.test(s)) s = `'${s}`;
      return `"${s.replace(/"/g, '""')}"`;
    };

    // Create CSV content
    const csvContent = [
      ['Çalışan', 'Email', 'Giriş', 'Çıkış', 'Çalışma Saati', 'Durum', 'Konum'],
      ...attendanceRecords.map(record => [
        record.employeeName || '',
        record.employeeEmail || '',
        safeFmt(record.checkInTime, 'dd/MM/yyyy HH:mm'),
        record.checkOutTime
          ? safeFmt(record.checkOutTime, 'dd/MM/yyyy HH:mm') + (record.autoCheckout ? ' (Otomatik Çıkış)' : '')
          : 'Devam ediyor',
        formatDurationTr(record.duration),
        record.status,
        record.workLocation || ''
      ])
    ].map(row => row.map(esc).join(',')).join('\r\n');

    // Download CSV file (BOM ile → Excel Türkçe karakterleri doğru okur)
    const blob = new Blob([String.fromCharCode(0xFEFF) + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `devam-kayitlari-${branchName}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PRESENT': return <CheckCircle />;
      case 'LATE': return <Schedule />;
      case 'ABSENT': return <Cancel />;
      case 'EARLY_LEAVE': return <ExitToApp />;
      case 'OVERTIME': return <AccessTime />;
      case 'COMPLETED': return <CheckCircle />;
      case 'ACTIVE': return <AccessTime />;
      case 'PENDING': return <Schedule />;
      case 'HALF_DAY': return <Schedule />;
      default: return <Today />;
    }
  };


  // Pagination
  const paginatedRecords = attendanceRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Devam kayıtları yükleniyor...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {branchName} - Devam Kayıtları
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={attendanceRecords.length === 0}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              Yenile
            </Button>
          </Stack>
        </Stack>

        {/* Filters */}
        <Card>
          <CardHeader title="Filtreleme" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Başlangıç Tarihi"
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="Tarih değiştirince otomatik yenilenir"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bitiş Tarihi"
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="Tarih değiştirince otomatik yenilenir"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Çalışan Ara"
                  value={searchEmployee}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Durum</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Durum"
                    onChange={handleStatusChange}
                  >
                    {ATTENDANCE_STATUS.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert severity="error">
            {error}
            <Button onClick={handleRefresh} sx={{ ml: 2 }}>
              Tekrar Dene
            </Button>
          </Alert>
        )}

        {/* Table */}
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Çalışan</TableCell>
                    <TableCell>Giriş</TableCell>
                    <TableCell>Çıkış</TableCell>
                    <TableCell>Çalışma Saati</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Konum</TableCell>
                    <TableCell>Sebep</TableCell>
                    <TableCell>Sapma</TableCell>
                    <TableCell align="right">İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRecords.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {record.employeeName?.charAt(0) || '?'}
                          </Avatar>
                          <Stack>
                            <Typography variant="subtitle2">
                              {record.employeeName || 'Bilinmeyen'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {record.employeeEmail}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <Typography variant="body2">
                            {safeFmt(record.checkInTime, 'dd/MM/yyyy')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {safeFmt(record.checkInTime, 'HH:mm')}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {record.checkOutTime ? (
                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              {safeFmt(record.checkOutTime, 'dd/MM/yyyy')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {safeFmt(record.checkOutTime, 'HH:mm')}
                            </Typography>
                            {record.autoCheckout && (
                              <Tooltip title={AUTO_CHECKOUT_TOOLTIP_TR}>
                                <Label variant="soft" color="warning">{AUTO_CHECKOUT_LABEL_TR}</Label>
                              </Tooltip>
                            )}
                          </Stack>
                        ) : (
                          <Label variant="soft" color="info">Devam ediyor</Label>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Ongoing shift: show live elapsed time; completed: enriched duration.
                          const ongoing = !record.checkOutTime;
                          const mins = ongoing
                            ? durationMinutesFromTimestamps(record.checkInTime, new Date().toISOString())
                            : record.duration;
                          if (mins == null || mins <= 0) {
                            return <Typography variant="body2" color="text.secondary">-</Typography>;
                          }
                          return (
                            <Typography variant="body2">
                              {formatDurationTr(mins)}{ongoing ? ' · devam ediyor' : ''}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Label
                          variant="soft"
                          color={attendanceStatusColor(record.status)}
                          startIcon={getStatusIcon(record.status)}
                        >
                          {attendanceStatusLabel(record.status)}
                        </Label>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {record.workLocation || 'Bilinmeyen'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {record.reason ? (
                          <Tooltip title={record.reasonDescription || ''}>
                            <Typography variant="body2">
                              {ATTENDANCE_REASON_LABELS_TR[record.reason]}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {record.deviationMinutes != null ? (
                          <Typography
                            variant="body2"
                            color={record.deviationMinutes > 0 ? 'warning.main' : 'info.main'}
                          >
                            {record.deviationMinutes > 0 ? '+' : ''}
                            {record.deviationMinutes}dk
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Detaylar">
                          <IconButton size="small">
                            <Iconify icon="solar:eye-bold" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedRecords.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Seçilen kriterlere uygun devam kaydı bulunamadı
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            component="div"
            count={attendanceRecords.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Sayfa başına satır:"
          />
        </Card>
      </Stack>
    </Box>
  );
};

export default BranchAttendanceTab;
